import React, { useEffect, useRef, useState, useCallback } from "react";
import { lang } from "src/language/Strings";
import Image from "./Image";
import useFileMutation from "src/api/hooks/useFileMutation";
import { useParams } from "react-router-dom";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import { useFilesForCompanyId } from "src/api/hooks/useFiles";
import { Modal } from "./Modal";
import { usePosition } from "src/api/hooks/usePosition";
import { UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/api/queryKeys";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import { portalApiClient } from "src/api/apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { ShowToast } from "./Toast";
import { uploadFileToBlob } from "src/api/uploadFileToBlob";
import AutoCompleteTextField from "./AutoCompleteTextField";
import { InputLabel, SwipeableDrawer } from "@mui/material";

// Custom hook for managing upload progress with throttling
function useUploadProgress() {
  const [progress, setProgress] = useState<Record<string, number>>({});
  const progressRef = useRef<Record<string, number>>({});
  const animationFrameRef = useRef<number | null>(null);
  const updateProgress = useCallback(
    (fileName: string, newProgress: number) => {
      // Store the latest progress
      progressRef.current[fileName] = newProgress;

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Schedule update for next frame
      animationFrameRef.current = requestAnimationFrame(() => {
        setProgress({ ...progressRef.current });
      });
    },
    []
  );

  const resetProgress = useCallback((fileName: string) => {
    setProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return { progress, updateProgress, resetProgress };
}

export default function FileInput({
  name,
  required,
  onChange,
  onUploadProgress,
  multiple,
  showOnlyButton,
  groupId,
  ...props
}: {
  name: string;
  required: boolean;
  groupId: number;
  props?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (files: SasFileResult[]) => void;
  onUploadProgress?: (progress: number, fileName: string) => void;
  multiple?: boolean;
  showOnlyButton?: boolean;
}) {
  const [files, setFiles] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeFile, setActiveFile] = useState<any | null>(null);
  const multipleInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>("");
  const { data: companyFiles } = useFilesForCompanyId();

  const { companyId } = useParams();
  const {
    progress: uploadProgress,
    updateProgress,
    resetProgress,
  } = useUploadProgress();

  // Use useCallback to prevent unnecessary re-renders
  const handleUploadProgress = useCallback(
    (progress: number, fileName: string) => {
      updateProgress(fileName, Math.round(progress * 100));
      if (onUploadProgress) {
        onUploadProgress(progress, fileName);
      }
    },
    [updateProgress]
  );

  const { create: createFile } = useFileMutation(handleUploadProgress);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    setError("");
    if (e.target.files && e.target.files.length > 0) {
      const newFile = {
        file: e.target.files[0],
        fileName: e.target.files[0].name,
        comment: "",
        depiction: "",
        companyId: Number(companyId),
      };
      setActiveFile(newFile as any);
    }
  }

  function handleFileRemoval(filename: string) {
    console.log("filename: ", filename, "Files: ",files)
    setFiles((prev) => prev.filter((f) => f.fileName !== filename));
    resetProgress(filename);

    const dataTransfer = new DataTransfer();
    const currentFiles = multipleInputRef.current?.files;

    if (currentFiles) {
      for (let i = 0; i < currentFiles.length; i++) {
        if (currentFiles[i].name !== filename) {
          dataTransfer.items.add(currentFiles[i]);
        }
      }
      if (multipleInputRef.current) {
        multipleInputRef.current.files = dataTransfer.files; // Update the FileList after removal
      }
    }
  }
  useEffect(() => {
    if (createFile.isSuccess){
      setFiles((prev) => [
        ...prev,
        {
          file: activeFile?.file,
          url: createFile.data?.files?.[0],
          fileName: files.some(f => f.fileName === createFile.data?.file?.name) ? `${createFile.data?.file?.name.replace(/\.[^/.]+$/, "")}_(${files.filter(f => f.fileName === createFile.data?.file?.name).length + 1})${createFile.data?.file?.name.split(".")[1]}` : createFile.data?.file?.name ?? activeFile?.fileName,
          companyId: Number(companyId),
          comment: createFile.data?.comment ?? activeFile?.comment,
          depiction: createFile.data?.depiction ?? activeFile?.depiction,
          id: createFile.data?.id,
          deviationId: createFile.data?.deviationId ?? activeFile?.deviationId,
          position: createFile.data?.position ?? activeFile?.position,
        },
      ]);
      setActiveFile(null);
    } else if (createFile.isError) {
      setError(createFile.error.message);
    }
  }, [createFile.isSuccess]);

  useEffect(() => {
    if (onChange) {
      onChange(files);
    }
  }, [files, activeFile, createFile.isSuccess]);

  return (
    <>
      <div
        className={`border-2 border-Grey7 rounded-lg p-2 h-40 grid grid-rows-4 ${
          showOnlyButton ? "grid-rows-1 border-0" : ""
        }`}
      >
        <div className="w-full overflow-y-auto row-span-3">
          {files.map((f, index) => (
            <div
              key={index}
              className={`grid grid-cols-6 relative ${
                uploadProgress[f.fileName!] && uploadProgress[f.fileName!] < 100
                  ? "opacity-80"
                  : ""
              }`}
            >
              <div
                className={`absolute h-full transition-all duration-150 ease-out pointer-events-none ${
                  uploadProgress[f.fileName!]
                    ? "opacity-50 bg-PrimaryGreen"
                    : ""
                }`}
                style={{
                  width: `${uploadProgress[f.fileName!] || 0}%`,
                }}
              >
                {" "}
              </div>
              <span className="ml-2 truncate col-span-3 relative z-10">{f.fileName}</span>
              <span className="flex col-span-2 items-center justify-center text-xs text-gray-600 relative z-10">
                {uploadProgress[f.fileName!]
                  ? `${Math.round(uploadProgress[f.fileName!])}%`
                  : ""}
              </span>
              <span className="flex col-span-1 items-center justify-end relative z-10">
                <i
                  className="fa-solid fa-times text-lg ml-2 cursor-pointer mr-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileRemoval(f.fileName!);
                  }}
                ></i>
              </span>
            </div>
          ))}
        </div>
        <input
          ref={inputRef}
          type="file"
          name={name}
          id="file-input"
          className="hidden"
          onChange={handleFileChange}
        />

        <div>
          <button
            id={`${name}-file-input-button`}
            name={"file-input-button"}
            onClick={(e) => {
              e.preventDefault();
              if (inputRef.current) inputRef.current.click();
            }}
            className="border-2 border-Grey7 rounded pt-1 pb-1 pl-2 pr-2"
          >
            {lang.addImage}
          </button>
        </div>
        <div className="h-4 w-full text-sm text-PrimaryRed pt-1">{error}</div>
      </div>
      {activeFile && (
        <ActiveFileModal
          activeFile={activeFile}
          setActiveFile={setActiveFile}
          companyFiles={companyFiles || []}
          onClick={createFile.mutate as any}
        />
      )}
    </>
  );
}

export function FileInputButton({ id }: { id: string }) {
  const { data: currentItem } = useProjectVariationById(id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const createFile = useCallback(
    async (
      data: Omit<SasFileResult, "files" | "url" | "id" | "imageResponse">
    ) => {
      if (process.env.VITE_NODE_ENV === "cypress") {
        setActiveFile(null);
        queryClient.invalidateQueries({
          queryKey: queryKeys.selectedVariation(id),
        });
        return {
          id: 1,
          fileName: data.fileName,
          files: [],
        };
      }
      const result = await portalApiClient.post<SasFileResult>(
        apiUrls.file.create.url(),
        data
      );
      if (result.status === 200) {
        await uploadFileToBlob({ ...data, ...result.data });
        ShowToast(lang.fileCreatedSuccessfully, "success");
        setActiveFile(null);
        queryClient.invalidateQueries({
          queryKey: queryKeys.selectedVariation(id),
        });

        return result.data;
      } else {
        ShowToast(lang.fileCreatedError, "error");
      }
      return null;
    },
    [id, currentItem]
  );
  const [activeFile, setActiveFile] = useState<SasFileResult | null>(null);
  const { data: companyFiles } = useFilesForCompanyId();
  const position = usePosition();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setActiveFile({
        file: file,
        companyId: Number(companyId),
        comment: "",
        depiction: "",
        fileType: FileType.Image,
        fileName: file.name,
        files: [],
        boundingBox: "",
        exif: "",
        deviationId: Number(id),
        position: {
          latitude: position.latitude || 0,
          longitude: position.longitude || 0,
          adress: currentItem?.title || "",
          geometry: `{"type":"Point","coordinates":[${
            position.longitude || 0
          },${position.latitude || 0}]}`,
        },
      });
    }
  };

  return (
    <div
      id={`${id}-file-input-div`}
      className="border-2 border-Grey7 rounded pt-1 pb-1 pl-2 pr-2"
    >
      <input
        ref={fileInputRef}
        type="file"
        id={`${id}-file-input`}
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        id={`${id}-file-input-button`}
        name={"file-input-button"}
        onClick={() => {
          if (fileInputRef.current) fileInputRef.current.click();
        }}
      >
        {lang.addImage}
      </button>
      {activeFile && (
        <ActiveFileModal
          activeFile={activeFile}
          setActiveFile={setActiveFile}
          companyFiles={companyFiles || []}
          onClick={async (sasFile) => {
            const file = await portalApiClient.post<SasFileResult>(
              apiUrls.file.create.url(),
              sasFile
            );
            uploadFileToBlob({ ...sasFile, ...file.data }).then(() => {
              setActiveFile(null);
              queryClient.invalidateQueries({
                queryKey: queryKeys.selectedVariation(id),
              });
            });
          }}
        />
      )}
    </div>
  );
}

export function ActiveFileModal({
  activeFile,
  setActiveFile,
  companyFiles,
  onClick,
}: {
  activeFile: any;
  setActiveFile: (file: any) => void;
  companyFiles: any[];
  onClick: (sasFile: SasFileResult) => void;
}) {
  return (
    // <Modal onOutsideClick={() => setActiveFile(null)} >
      <SwipeableDrawer
        anchor="bottom"
        open={activeFile !== null}
        onClose={() => setActiveFile(null)}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            height: "93vh",
            borderRadius: "16px 16px 0 0",
            padding: "26px",
          },
        }}
      >
        
      <div className="flex flex-row gap-2 w-full  justify-center items-center h-full">
        <Image filename={activeFile?.files?.[0] || URL.createObjectURL(activeFile?.file) || ""} imageClassName="max-h-80" />
      </div>

      <AutoCompleteTextField label={lang.objectType} name="objectType" customOptions={companyFiles?.map((f) => f.depiction).reduce((acc, f) => acc.includes(f) ? acc : [...acc, f], []) || []} />
      <AutoCompleteTextField label={lang.description} name="description" customOptions={companyFiles?.map((f) => f.comment).reduce((acc, f) => acc.includes(f) ? acc : [...acc, f], []) || []} />

      <AutoCompleteTextField label={lang.action} name="action" customOptions={companyFiles?.map((f) => f.action).reduce((acc, f) => acc.includes(f) ? acc : [...acc, f], []) || []} />


      <div className="flex justify-center gap-2 mt-4">
        <button
          className="text-white rounded-lg p-2 border-2 bg-PrimaryGreen text-White"
          id="cancel-file-input-button"
          onClick={() => {
            setActiveFile(null);
          }}
        >
          {lang.cancel}
        </button>
        <button
          className="text-white rounded-lg p-2 border-2 bg-PrimaryGreen text-White"
          id="create-file-input-button"
          onClick={() => {
            if (activeFile?.groupId === null) {
              delete activeFile.groupId;
            }
            const sasFile = {
              ...activeFile,
              comment:
                (document.getElementById("comment") as HTMLInputElement)
                  ?.value || "",
              depiction:
                (document.getElementById("type") as HTMLInputElement)?.value ||
                "",
                action: (document.getElementById("action") as HTMLInputElement)?.value || "",
            } as SasFileResult;
            onClick(sasFile);
          }}
          type="button"
        >
          {lang.save}
        </button>
      </div>
    {/* </Modal> */}
    </SwipeableDrawer>
  );
}
